import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { Divider, Text, View } from "@towersystems/roam-common-native";
import { memo, useEffect, useState } from "react";
import { FlatList, ListRenderItemInfo } from "react-native";
import { FlatListSearchableSkeleton } from "../../../../../../components/FlatListSearchableSkeleton";
import StockCountItem from "../../../../../../utilities/data/model/StockCountItem";
import StockCountLine from "../../../../../../utilities/data/model/StockCountLine";
import { UseAwaitReturn } from "../../../../../../utilities/use-await";
import { StockCountHistoryListItem } from "../StockCountHistoryListItem";

const MISSING_HISTORY_ITEMS_LABEL = "No stock count history to show";

type StockCountProductContentRendererProps = {
  stockCountLine: StockCountLine;
  showLoading?: boolean;
};

export const StockCountHistoryContent = memo(
  ({ stockCountLine, showLoading }: StockCountProductContentRendererProps) => {
    const database = useDatabase();

    const [stockCountItemsState, setStockCountItemsState] = useState<
      UseAwaitReturn<StockCountItem[]>
    >({ isLoading: false, data: [], isError: false });

    useEffect(() => {
      if (!stockCountLine) return;
      database.collections
        .get<StockCountItem>("stock_count_items")
        .query(
          Q.on("stock_count_lines", Q.where("id", stockCountLine.id)),
          Q.sortBy("created_at", Q.asc),
          Q.take(100)
        )
        .fetch()
        .then((items) => {
          setStockCountItemsState({
            isLoading: false,
            data: items,
            isError: false,
          });
        })
        .catch((err) => {
          setStockCountItemsState({
            isLoading: false,
            data: [],
            isError: true,
          });
        });

      return () => {};
    }, [stockCountLine.id]);

    function renderItem({ item, index }: ListRenderItemInfo<StockCountItem>) {
      if (!stockCountLine) return null;
      const items = stockCountItemsState.data;
      const hasDivider = !!items && index !== items.length - 1;
      return (
        <>
          {hasDivider && <Divider marginVertical={0} />}
          <StockCountHistoryListItem
            stockCountItem={item}
            stockCountLine={stockCountLine}
          />
        </>
      );
    }

    if (stockCountItemsState.isLoading || showLoading) {
      return <FlatListSearchableSkeleton searchbar={false} />;
    }

    // *NOTE* inverted flag uses scale transform to flip the list - which breaks ability to scroll on the web
    // workaround here is to use flexDirection: column-reverse on the list and contentContainerStyle
    // thus item separator component cant be used
    return stockCountItemsState.data?.length ? (
      <View>
        <FlatList
          scrollEnabled={true}
          data={stockCountItemsState.data}
          inverted={false}
          renderItem={renderItem}
          windowSize={3}
          initialNumToRender={20}
          style={{ flexDirection: "column-reverse" }}
          contentContainerStyle={{ flexDirection: "column-reverse" }}
        />
      </View>
    ) : (
      <Text>{MISSING_HISTORY_ITEMS_LABEL}</Text>
    );
  }
);
